import { useState, useEffect } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'

interface Client {
  id: string
  name: string
  slug: string
}

interface Site {
  id: string
  name: string
  slug: string
}

interface User {
  id: string
  full_name: string
  slug: string
}

interface Props {
  onClose: () => void
  onCreated: () => void
}

export function TicketCreateModal({ onClose, onCreated }: Props) {
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')
  const [clientId, setClientId] = useState('')
  const [siteId, setSiteId] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [clients, setClients] = useState<Client[]>([])
  const [sites, setSites] = useState<Site[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [saving, setSaving] = useState(false)
  const { toast, toastError } = useToast()

  useEffect(() => {
    api.get<{ data: Client[] }>('/clients?limit=200').then(r => setClients(r.data || [])).catch(() => {})
    api.get<{ data: User[] }>('/users').then(r => setUsers(r.data || [])).catch(() => {})
  }, [])

  // Load sites when client changes
  useEffect(() => {
    if (!clientId) { setSites([]); return }
    api.get<{ data: Site[] }>(`/sites?client_id=${clientId}&limit=100`).then(r => setSites(r.data || [])).catch(() => {})
  }, [clientId])

  const handleSubmit = async () => {
    if (!subject.trim()) { toast('Subject is required', 'error'); return }
    setSaving(true)
    try {
      await api.post('/tickets', {
        subject, description: description || undefined, priority,
        client_id: clientId || undefined, site_id: siteId || undefined,
        assigned_to: assignedTo || undefined, source: 'manual',
      })
      toast('Ticket created')
      onCreated()
    } catch (err) {
      toastError('Failed to create ticket', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div
        style={{
          background: 'var(--bg-surface)', borderRadius: 12,
          padding: 24, width: 520, maxHeight: '80vh', overflow: 'auto',
          boxShadow: 'var(--shadow-xl)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, margin: 0 }}>New Ticket</h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Subject *</label>
            <input type="text" className="v3-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of the issue" style={{ width: '100%' }} autoFocus />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
            <textarea className="v3-textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Detailed description..." style={{ width: '100%', minHeight: 80, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Client</label>
              <div className="v3-select-wrap">
                <select className="v3-select" value={clientId} onChange={e => { setClientId(e.target.value); setSiteId('') }} style={{ width: '100%' }}>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Site</label>
              <div className="v3-select-wrap">
                <select className="v3-select" value={siteId} onChange={e => setSiteId(e.target.value)} style={{ width: '100%' }} disabled={!clientId}>
                  <option value="">Select site...</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Priority</label>
              <div className="v3-select-wrap">
                <select className="v3-select" value={priority} onChange={e => setPriority(e.target.value)} style={{ width: '100%' }}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Assign To</label>
              <div className="v3-select-wrap">
                <select className="v3-select" value={assignedTo} onChange={e => setAssignedTo(e.target.value)} style={{ width: '100%' }}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
                <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <Button onClick={handleSubmit} disabled={!subject.trim()} loading={saving}>
            Create Ticket
          </Button>
        </div>
      </div>
    </div>
  )
}
